import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, flash, post } from "../lib/api";
import { dayLabel, useLive } from "../lib/time";
import { ClientChip } from "../components/bits";
import { ClientForm } from "../components/ClientForm";
import { ClientSheet } from "../components/ClientSheet";
import { hm, pct, plural, flagged, monthName, monthShort, nextMonthStart, az, prevMonths, refPct, GROUPS, GROUP_LABEL, GroupLabel, SplitBar, groupOf, TEAM_NAME } from "../lib/hours";

const byHours = (a, b) => b.minutes - a.minutes || az(a.name, b.name);
const Num = ({ children }) => <b className="num">{children}</b>;

function Strip({ values, months, pick, untracked }) {
  return (
    <div className="mstrip">
      <div className="ms-grid" aria-hidden="true">
        {values.map((v, i) => (
          <div key={months[i]}>
            <div className="ms-bar">{!untracked[i] && <span className={months[i] === pick ? "on" : ""} style={{ width: `${v > 0 ? Math.max(4, refPct(v)) : 0}%` }} />}</div>
            <div className={`ms-m ${months[i] === pick ? "on" : ""}`}>{monthShort(months[i])}{i === 2 && <i title="so far" />}</div>
            <div className={`num ms-h ${months[i] === pick ? "on" : ""}`}>{untracked[i] ? "Not tracked" : hm(v)}</div>
          </div>))}
      </div>
      <p className="sr-only">Last 3 months: {values.map((v, i) => `${monthName(months[i])} ${untracked[i] ? "not tracked" : hm(v)}${i === 2 ? " so far" : ""}`).join(", ")}.</p>
    </div>
  );
}

function Row({ r, running, month, months, trend, untracked, byId, onOpen }) {
  const short = (id) => byId[id]?.short || id;
  const groups = GROUPS.map((g) => ({ g, list: r.people.filter((p) => groupOf(p.team) === g).sort((x, y) => y.minutes - x.minutes || az(short(x.userId), short(y.userId))) })).filter((x) => x.list.length);
  const flag = flagged(r);
  const split = [["design", r.designMinutes], ["video", r.videoMinutes], ["other", r.otherMinutes]].filter(([, m]) => m > 0);
  return (
    <li className="ckc-li" data-testid={`client-row-${r.code}`}>
      <div className="ckc-row">
        <div className="ckc-id">
          <div className="ckc-name"><ClientChip code={r.code} name={r.name} />
            <button className="ckc-open" onClick={onOpen} aria-label={`${r.name}: open details`} data-testid={`client-open-${r.code}`}>{r.name}</button></div>
          <div className="hint0" data-testid={`client-pocs-${r.code}`}>Serviced by {r.owners?.length ? r.owners.map(short).join(", ") : "nobody yet"}</div>
          {r.headcount > 0 && <div className="hint0">{plural(r.headcount, "person", "people")} on it</div>}
        </div>
        <div className="ckc-tot">
          <div className="lbl xl-hide">Total hours</div>
          <div className={`num big44 ${r.minutes ? "" : "zero"}`}>{hm(r.minutes)}</div>
          <div className="sr-only">{running ? `logged on ${r.name} so far this month` : `logged on ${r.name} in ${monthName(month)}`}</div>
          {r.minutes > 0 && <><div className="sb-wrap"><SplitBar c={r} /></div>
            <div className="num hint0">{split.map(([g, m]) => `${g === "other" ? "Other" : GROUP_LABEL[g]} ${hm(m)}`).join(" / ")}</div></>}
          <Strip values={months.map((_, i) => trend[i]?.minutes || 0)} months={months} pick={month} untracked={untracked} />
        </div>
        <div className="ckc-crew">
          <div className="lbl xl-hide">Who worked on it</div>
          {groups.length ? groups.map(({ g, list }) => (
            <div key={g} className="crew-g"><GroupLabel g={g} className="w74" />
              <ul aria-label={`${GROUP_LABEL[g]}, hours per person`}>{list.map((p) => (
                <li key={`${p.userId}|${p.team}`} title={byId[p.userId] ? `${byId[p.userId].name}, ${byId[p.userId].designation}` : undefined}>
                  {short(p.userId)}{g === "other" && <span className="i2"> ({TEAM_NAME[p.team] || p.team})</span>} <b className="num">{hm(p.minutes)}</b></li>))}</ul></div>))
            : <p className="hint0">Nobody has logged time on {r.name} {running ? "yet this month" : `in ${monthName(month)}`}.</p>}
        </div>
        <div className="ckc-rev">
          <div className="lbl xl-hide">Revision hours</div>
          <div className="rev-n">{flag && <span className="warn-i" aria-hidden="true">▲</span>}<span className={`num ${r.revisionMinutes ? "" : "zero"}`}>{hm(r.revisionMinutes)}</span></div>
          {r.minutes > 0 && <div className={`hint0 ${flag ? "strong" : ""}`}>{pct(r.revisionMinutes / r.minutes)} of its hours{flag && ", over 30%"}</div>}
        </div>
      </div>
    </li>
  );
}

function RemovedFold({ list, canRestore, onRestore }) {
  const [open, setOpen] = useState(false);
  if (!list.length) return null;
  return (
    <section className="everyone" data-testid="removed-clients-fold">
      <h2><button aria-expanded={open} onClick={() => setOpen(!open)} data-testid="removed-clients-toggle">{open ? "▴" : "▾"} Removed clients <span className="num i2">{list.length}</span></button></h2>
      {open && <><p className="hint0">Hidden from Clients and from new cards. Their past hours stay in the Excel export.</p>
        <ul className="ep-list rm-list">{list.map((c) => (
          <li key={c.code} data-testid={`removed-${c.code}`}><span className="chip-l"><ClientChip code={c.code} name={c.name} /><span className="chip-n">{c.name}</span></span>
            <span className="i2">Removed {dayLabel(new Date(c.removedAt).toISOString().slice(0, 10))}</span>
            {canRestore && <button className="btn-ghost sm" onClick={() => onRestore(c)} data-testid={`restore-${c.code}`}>Restore</button>}</li>))}</ul></>}
    </section>
  );
}

const Head = () => <div className="ckc-row ckc-head"><span className="lbl">Client</span><span className="lbl">Total hours</span><span className="lbl">Who worked on it</span><span className="lbl">Revision hours</span></div>;

function EveryPerson({ rows, byId, month, running }) {
  const [open, setOpen] = useState(false);
  const m = {};
  rows.forEach((r) => r.people.forEach((p) => {
    const k = `${p.userId}|${p.team}`;
    m[k] = m[k] || { id: p.userId, team: p.team, min: 0, rev: 0, clients: new Set() };
    m[k].min += p.minutes; m[k].rev += p.revisionMinutes; m[k].clients.add(r.code);
  }));
  const name = (id) => byId[id]?.name || id;
  const list = Object.values(m).sort((a, b) => b.min - a.min || az(name(a.id), name(b.id)));
  const count = new Set(list.map((x) => x.id)).size;
  if (!count) return null;
  return (
    <section className="everyone" data-testid="each-person-fold">
      <h2><button aria-expanded={open} onClick={() => setOpen(!open)} data-testid="each-person-toggle">{open ? "▴" : "▾"} Each person's hours in {monthName(month)}{running ? " so far" : ""} <span className="num i2">{plural(count, "person", "people")}</span></button></h2>
      {open && <><p className="hint0">Every retainer and project added up per person, most hours first. Internal CK work is not counted.</p>
        {GROUPS.map((g) => { const gl = list.filter((x) => groupOf(x.team) === g); return gl.length > 0 && (
          <div key={g} className="ep-g"><GroupLabel g={g} hours={gl.reduce((s, x) => s + x.min, 0) / 60} />
            <ul className="ep-list">{gl.map((x) => (
              <li key={`${x.id}|${x.team}`}><span className="trunc" title={byId[x.id] ? `${byId[x.id].name}, ${byId[x.id].designation}` : ""}>{name(x.id)}{g === "other" && <span className="i2"> ({TEAM_NAME[x.team] || x.team})</span>}</span>
                <span className="num"><b>{hm(x.min)}</b><span className="i2"> {x.rev > 0 ? `${hm(x.rev)} rev, ` : ""}{plural(x.clients.size, "client", "clients")}</span></span></li>))}</ul></div>); })}</>}
    </section>
  );
}

export default function Clients({ me, go }) {
  const [byMonth, setByMonth] = useState({});
  const [months, setMonths] = useState(null);
  const [pick, setPick] = useState(null);
  const [all, setAll] = useState([]);
  const [people, setPeople] = useState([]);
  const [openCode, setOpenCode] = useState(null);
  const [form, setForm] = useState(null);
  const [quiet, setQuiet] = useState(false);
  const tabs = useRef(null);
  const load = useCallback(async () => {
    const r0 = await api("/clients/hours");
    if (!r0.ok) return;
    const ms = prevMonths(r0.data.month);
    const rest = await Promise.all(ms.slice(0, 2).map((m) => api(`/clients/hours?month=${m}`)));
    if (!rest.every((x) => x.ok && x.data?.clients)) return;
    setMonths(ms);
    setByMonth({ [ms[0]]: rest[0].data, [ms[1]]: rest[1].data, [ms[2]]: r0.data });
    const c = await api("/clients");
    if (c.ok) setAll(c.data.items);
  }, []);
  useEffect(() => {
    load();
    api("/people").then((r) => r.ok && setPeople(r.data.items));
  }, [load]);
  useLive(load, (t) => /^(timer|segment|autostop|client|task|day|person)\./.test(t) || t === "autostop");
  const byId = useMemo(() => Object.fromEntries(people.map((p) => [p.id, p])), [people]);
  if (!months) return <div className="loading" data-testid="clients-loading">Counting hours…</div>;
  const current = months[2];
  const month = pick && months.includes(pick) ? pick : current;
  const at = months.indexOf(month);
  const meta = byMonth[month];
  const running = month === current;
  const rows = meta.clients;
  const start = meta.dataStart;
  const untracked = months.map((m) => byMonth[m].to < start);
  const beforeData = meta.to < start;
  const partial = !beforeData && meta.from < start;
  const trend = (code) => months.map((m) => byMonth[m].clients.find((c) => c.code === code));
  const retainers = rows.filter((r) => r.type === "retainer").sort(byHours);
  const active = rows.filter((r) => r.type === "project" && r.minutes > 0).sort(byHours);
  const idle = rows.filter((r) => r.type === "project" && !r.minutes).sort((a, b) => az(a.name, b.name));
  const shown = GROUPS.filter((g) => rows.some((r) => (g === "design" ? r.designMinutes : g === "video" ? r.videoMinutes : r.otherMinutes) > 0));
  const anyFlag = rows.some(flagged);
  const openRow = openCode && rows.find((r) => r.code === openCode);
  async function restore(c) {
    const r = await post(`/clients/${c.code}/restore`);
    flash(r.ok ? r.data.message : r.error.message);
    load();
  }
  async function remove(c) {
    const r = await post(`/clients/${c.code}/remove`);
    if (!r.ok) return r.error.message;
    setOpenCode(null);
    flash(r.data.message, () => restore(c));
    load();
    return null;
  }
  const onKey = (e) => {
    const step = { ArrowRight: 1, ArrowLeft: -1, Home: -9, End: 9 }[e.key];
    if (!step) return;
    e.preventDefault();
    const n = months[Math.max(0, Math.min(2, at + step))];
    setPick(n);
    tabs.current?.querySelector(`#ckc-tab-${n}`)?.focus();
  };
  const list = (l) => l.map((r) => <Row key={r.code} r={r} running={running} month={month} months={months} trend={trend(r.code)} untracked={untracked} byId={byId} onOpen={() => setOpenCode(r.code)} />);
  return (
    <div className="clients" data-testid="clients-page">
      <div className="chead">
        <div><h1 className="stackh"><span className="hl-ink">WHERE</span> <span className="hl-mari">THE HOURS</span> <span className="hl-g">WENT</span></h1>
          <p className="greybar">who put how many hours on each client, month by month</p></div>
        <div className="cmonths">
          <div className="mtabs" role="tablist" aria-label="Month" ref={tabs} onKeyDown={onKey}>
            {months.map((m) => (
              <button key={m} id={`ckc-tab-${m}`} role="tab" aria-selected={m === month} aria-controls="ckc-panel" tabIndex={m === month ? 0 : -1}
                className={m === month ? "on" : ""} onClick={() => setPick(m)} data-testid={`month-${m}`}>
                <span aria-hidden="true">{monthShort(m)}</span>{m === current && <i aria-hidden="true" />}
                <span className="sr-only">{monthName(m)} {m.slice(0, 4)}{m === current ? ", this month, so far" : ", closed"}</span></button>))}
          </div>
          <p className="hint0 mnote" data-testid="month-note">{running ? `Counting since 1 ${monthName(month)}. On ${nextMonthStart(month)} a new month starts at 0h 00m, and ${monthName(month)} stays here as a closed month.`
            : at === 0 ? `${monthName(month)} is closed: these are its final hours. It leaves this page on ${nextMonthStart(current)} and stays in the Excel export.` : `${monthName(month)} is closed: these are its final hours.`}
            {partial && ` Counted from ${dayLabel(start)}, the first day in CK Tiffin.`}</p>
        </div>
        {me.caps.addWork && <button className="btn-solid ml-auto" onClick={() => setForm({})} data-testid="add-client-button">+ Add client</button>}
      </div>
      <div role="tabpanel" id="ckc-panel" aria-labelledby={`ckc-tab-${month}`}>
        {beforeData ? (
          <div className="prelive" data-testid="before-go-live"><p className="big19">CK Tiffin started on {dayLabel(start)}, so there are no hours for {monthName(month)}.</p><p className="hint0">Pick a later month above.</p></div>
        ) : <>
          <section className="csec">
            <div className="csec-h"><h2>Retainers <span className="num i2">{retainers.length}, most hours first</span></h2>
              <div className="legend">{shown.map((g) => <span key={g}><i className={`sb-${g}`} />{GROUP_LABEL[g]}</span>)}
                {anyFlag && <span><b className="warn-i">▲</b> Revisions above 30% of a client's hours</span>}</div></div>
            <div className="clist2" data-testid="retainers-list"><Head /><ul>{list(retainers)}</ul>{!retainers.length && <p className="hint0">No retainers yet.</p>}</div>
          </section>
          <section className="csec">
            <h2>Projects <span className="num i2">{active.length} with hours in {monthName(month)}, most hours first</span></h2>
            <div className="clist2" data-testid="projects-list">{active.length > 0 && <Head />}<ul>{list(active)}</ul>
              {!active.length && <p className="hint0 pad">No project has hours in {monthName(month)}{running ? " yet" : ""}.</p>}
              {idle.length > 0 && <div className="pfold"><button aria-expanded={quiet} onClick={() => setQuiet(!quiet)} data-testid="quiet-projects-toggle">{quiet ? "▴" : "▾"} {plural(idle.length, "project", "projects")} with no hours in {monthName(month)}</button>
                {quiet && <ul className="quiet">{idle.map((r) => <li key={r.code}><button onClick={() => setOpenCode(r.code)}><ClientChip code={r.code} name={r.name} /> {r.name}</button></li>)}</ul>}</div>}
            </div>
          </section>
          <EveryPerson rows={rows} byId={byId} month={month} running={running} />
          <RemovedFold list={all.filter((c) => c.removedAt)} canRestore={me.caps.offboard} onRestore={restore} />
        </>}
      </div>
      <div className="cfoot"><p>Clients keeps {monthShort(months[0])} to {monthShort(current)}; older months are in the Excel export. Internal CK work is not on this page, so in the export, Retainer hours plus Project hours match it. Hours show as hours and minutes (7h 30m), counted in whole minutes, the same way the export counts them.</p>
        {me.caps.export ? <button className="btn-ghost sm" onClick={() => go("export")} data-testid="clients-export-link">Export hours</button> : <span>Ask an owner for the Excel file.</span>}</div>
      {openRow && <ClientSheet key={openRow.code} c={openRow} client={all.find((x) => x.code === openRow.code)} trend={trend(openRow.code)} months={months} pick={month} running={running}
        people={people} untracked={untracked} onRemove={me.caps.offboard ? () => remove(openRow) : null} onClose={() => setOpenCode(null)} onEdit={me.caps.addWork ? () => setForm({ client: all.find((x) => x.code === openRow.code) }) : null} />}
      {form && <ClientForm me={me} client={form.client} all={all} onClose={() => setForm(null)}
        onDone={async (c, isNew) => { setForm(null); await load(); if (isNew && c.type !== "internal") setOpenCode(c.code); }} />}
    </div>
  );
}
